import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  CubeIcon, 
  PlusIcon, 
  ArrowsRightLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import StockReceiptModal from '../../components/inventory/StockReceiptModal';
import StockTransferModal from '../../components/inventory/StockTransferModal';
import InventoryHistory from '../../components/inventory/InventoryHistory';
import CreateWasteReportModal from '../../components/admin/CreateWasteReportModal';
import toast from 'react-hot-toast';

const InventoryManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  
  // Modals
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    loadData();
  }, [pagination.page, showLowStockOnly, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load dashboard stats
      const statsResponse = await axios.get('/api/inventory/dashboard');
      setDashboardStats(statsResponse.data);
      
      // Load products
      const productsResponse = await axios.get('/api/inventory/products', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          low_stock_only: showLowStockOnly,
          search: searchTerm || undefined
        }
      });
      
      setProducts(productsResponse.data.products);
      setPagination(prev => ({ ...prev, ...productsResponse.data.pagination }));
    } catch (error) {
      toast.error('שגיאה בטעינת נתוני המלאי');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveStock = () => {
    setSelectedProduct(null);
    setShowReceiptModal(true);
  };

  const handleTransferStock = (product) => {
    setSelectedProduct(product);
    setShowTransferModal(true);
  };

  const handleViewHistory = (product) => {
    setSelectedProduct(product);
    setShowHistoryModal(true);
  };

  const handleStockUpdated = () => {
    loadData();
    toast.success('המלאי עודכן בהצלחה');
  };

  const getStockBarColor = (metrics) => {
    if (metrics.is_low_stock) return 'bg-red-500';
    if (metrics.total_stock < metrics.min_stock_level) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading && !dashboardStats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען נתוני מלאי...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CubeIcon className="h-8 w-8 text-blue-600" />
            ניהול מלאי - מחסנים כפולים
          </h1>
          <p className="mt-2 text-gray-600">
            ניהול מלאי נפרד למחסן B2C (קמעונאי) ומחסן B2B (סיטונאי)
          </p>
        </div>

        {/* Stats Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">סה"כ מוצרים</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardStats.stats.total_products}
                  </p>
                </div>
                <CubeIcon className="h-12 w-12 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">מלאי B2C</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {dashboardStats.stats.b2c_stock_units.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">יחידות</p>
                </div>
                <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🛒</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">מלאי B2B</p>
                  <p className="text-2xl font-bold text-green-600">
                    {dashboardStats.stats.b2b_stock_units.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">יחידות</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏢</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">התראות מלאי נמוך</p>
                  <p className="text-2xl font-bold text-red-600">
                    {dashboardStats.stats.low_stock_count}
                  </p>
                </div>
                <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex flex-wrap gap-4">
          <button
            onClick={handleReceiveStock}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg font-semibold"
          >
            <PlusIcon className="h-5 w-5" />
            קבלת מלאי חדש
          </button>

          <button
            onClick={() => setShowWasteModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-lg font-semibold"
          >
            <ExclamationTriangleIcon className="h-5 w-5" />
            דוח בלאי
          </button>

          <button
            onClick={() => navigate('/admin/waste-reports')}
            className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold"
          >
            <ChartBarIcon className="h-5 w-5" />
            צפה בדוחות בלאי
          </button>

          <button
            onClick={() => setShowHistoryModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            <ClockIcon className="h-5 w-5" />
            היסטוריית תנועות
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="חיפוש לפי שם מוצר או SKU..."
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showLowStockOnly}
                  onChange={(e) => setShowLowStockOnly(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">הצג רק מלאי נמוך</span>
              </label>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    מוצר
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    מלאי B2C
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    מלאי B2B
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    סה"כ
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    התפלגות
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      {searchTerm || showLowStockOnly ? 'לא נמצאו מוצרים' : 'אין מוצרים במערכת'}
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {product.images && product.images[0] && (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{product.name}</div>
                            {product.category && (
                              <div className="text-sm text-gray-500">{product.category.name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {product.sku}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          product.metrics.b2c_low_stock 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-indigo-100 text-indigo-800'
                        }`}>
                          {product.stock_b2c}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          product.metrics.b2b_low_stock 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {product.stock_b2b}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-medium">
                        {product.metrics.total_stock}
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-full">
                          <div className="flex h-6 rounded-full overflow-hidden border border-gray-200">
                            <div
                              className="bg-indigo-500 flex items-center justify-center text-white text-xs font-medium"
                              style={{ width: `${product.metrics.b2c_percentage}%` }}
                              title={`B2C: ${product.metrics.b2c_percentage}%`}
                            >
                              {product.metrics.b2c_percentage > 15 && `${product.metrics.b2c_percentage}%`}
                            </div>
                            <div
                              className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
                              style={{ width: `${product.metrics.b2b_percentage}%` }}
                              title={`B2B: ${product.metrics.b2b_percentage}%`}
                            >
                              {product.metrics.b2b_percentage > 15 && `${product.metrics.b2b_percentage}%`}
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>🛒 B2C</span>
                            <span>🏢 B2B</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleTransferStock(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="העברת מלאי"
                          >
                            <ArrowsRightLeftIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleViewHistory(product)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                            title="היסטוריה"
                          >
                            <ClockIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  הקודם
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  הבא
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    מציג <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> עד{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>{' '}
                    מתוך <span className="font-medium">{pagination.total}</span> מוצרים
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    {[...Array(pagination.pages)].map((_, idx) => (
                      <button
                        key={idx + 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: idx + 1 }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.page === idx + 1
                            ? 'z-10 bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showReceiptModal && (
        <StockReceiptModal
          onClose={() => setShowReceiptModal(false)}
          onSuccess={handleStockUpdated}
        />
      )}

      {showTransferModal && selectedProduct && (
        <StockTransferModal
          product={selectedProduct}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedProduct(null);
          }}
          onSuccess={handleStockUpdated}
        />
      )}

      {showWasteModal && (
        <CreateWasteReportModal
          isOpen={showWasteModal}
          onClose={() => setShowWasteModal(false)}
          onReportCreated={() => {
            setShowWasteModal(false);
            handleStockUpdated();
          }}
        />
      )}

      {showHistoryModal && (
        <InventoryHistory
          product={selectedProduct}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default InventoryManagement;
